import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Home } from 'lucide-react';
import SEO from '@/components/SEO';

const Blog = () => {
  const [blogArticles, setBlogArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch('/api/blog-posts');
        if (!response.ok) throw new Error('Failed to fetch blog posts');
        const data = await response.json();
        // Transform to match existing format - keep date as ISO string for proper re-formatting
        const transformed = (data.posts || []).map(post => ({
          id: post.id,
          title: post.title,
          excerpt: post.excerpt,
          content: post.content,
          author: post.author,
          date: post.published_at,
          image: post.image_url || '/idnty-logo.svg',
          category: post.tags?.[0] || 'Company Update',
          readTime: '3 min read',
          linkedin_url: post.linkedin_url
        }));
        setBlogArticles(transformed);
      } catch (err) {
        setError(err.message);
        // Fallback to empty array if needed
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const categories = [...new Set(blogArticles.map(article => article.category))];

  useEffect(() => {
    setFilteredArticles(blogArticles);
  }, [blogArticles]);

  const handleCategoryFilter = (category) => {
    if (category === 'All') {
      setFilteredArticles(blogArticles);
    } else {
      const filtered = blogArticles.filter(article => article.category === category);
      setFilteredArticles(filtered);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading blog posts...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-600">Error loading blog posts: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Blog - Identity Management & Security Insights"
        description="Expert insights on identity management, access control, cybersecurity best practices, and compliance for growing companies."
        keywords="identity management blog, cybersecurity blog, access control tips, IAM best practices, security insights"
        ogTitle="IDNTY Blog - Identity Management & Security Insights"
        ogDescription="Expert insights on identity management, access control, cybersecurity best practices, and compliance for growing companies."
        canonicalUrl="https://idnty.one/blog"
      />
      {/* Header with Home Link */}
      <header className="bg-white border-b border-border py-4">
        <div className="container mx-auto px-4">
          <Link 
            to="https://www.idnty.one" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to IDNTY
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary/5 to-background py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
              IDNTY Blog
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Insights, guides, and best practices for identity security and access management
            </p>
          </div>
        </div>
      </section>

      {/* Blog Articles Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {blogArticles.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              No blog posts yet. Check back soon for updates!
            </div>
          ) : (
            <>
              {/* Category Filter */}
              {categories.length > 0 && (
                <div className="flex justify-center gap-2 mb-8 flex-wrap">
                  <button
                    onClick={() => handleCategoryFilter('All')}
                    className="px-4 py-2 rounded-full bg-primary text-white text-sm font-medium"
                  >
                    All
                  </button>
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => handleCategoryFilter(category)}
                      className="px-4 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-medium hover:bg-primary/10 transition-colors"
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredArticles.map((article) => (
                  <article 
                    key={article.id} 
                    className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col"
                  >
                    {/* Article Image */}
                    <div className="h-48 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center p-6">
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="h-24 w-auto object-contain"
                      />
                    </div>

                    {/* Article Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Category & Read Time */}
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                        <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs font-medium">
                          {article.category}
                        </span>
                        <span>{article.readTime}</span>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-semibold mb-3 line-clamp-2">
                        <Link 
                          to={`/blog/${article.id}`}
                          className="hover:text-primary transition-colors"
                        >
                          {article.title}
                        </Link>
                      </h2>

                      {/* Excerpt */}
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-1">
                        {article.excerpt}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span>{article.author}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                      </div>

{/* Read More Link */}
                       <div className="flex items-center justify-between">
                         <Link 
                           to={`/blog/${article.id}`}
                           className="inline-flex items-center text-primary font-medium hover:underline"
                         >
                           Read Article
                           <ArrowRight className="w-4 h-4 ml-1" />
                         </Link>
                         {article.linkedin_url && (
                           <a
                             href={article.linkedin_url}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-sm text-muted-foreground hover:text-primary"
                             title="View on LinkedIn"
                           >
                             View on LinkedIn
                           </a>
                         )}
                       </div>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
        </div>
      </section>

      {/* LinkedIn Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4 text-foreground">Stay Updated</h2>
            <p className="text-foreground mb-8">
              For more articles and insights on identity security, follow us on LinkedIn.
            </p>
            <a 
              href="https://www.linkedin.com/company/idnty1/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-md"
            >
              Follow us on LinkedIn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
